import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, RotateCcw } from "lucide-react";
import { api, setPageMeta } from "@/lib/api";
import { Reveal } from "@/components/Motion";
import { RACES, LEVELS, buildPlan } from "@/lib/plan";

const chip = (active) =>
  `font-mono-label px-4 py-3 border transition-colors duration-200 ${active ? "bg-white text-ink border-white" : "border-white/25 text-white/60 hover:border-white"}`;

const EmailPlan = ({ result }) => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/plan/email", {
        email, race: result.race, level: result.level, days: result.days,
        weeks_total: result.total, peak_miles: result.peakMiles, weeks: result.plan,
      });
      toast.success(res.data.message);
      setSent(true);
    } catch {
      toast.error("Something broke. Try again.");
    } finally {
      setLoading(false);
    }
  };
  if (sent) return <p data-testid="plan-email-sent" className="font-mono-label text-white/80 border border-white/25 px-5 py-4">✓ PLAN SENT. CHECK YOUR INBOX.</p>;
  return (
    <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3" data-testid="plan-email-form">
      <input
        data-testid="plan-email-input"
        type="email" required placeholder="EMAIL ADDRESS"
        value={email} onChange={(e) => setEmail(e.target.value)}
        className="flex-1 bg-transparent border-b border-white/30 py-3 text-sm placeholder:text-white/30 focus:border-white transition-colors duration-200"
      />
      <button type="submit" disabled={loading} data-testid="plan-email-submit" className="btn-invert font-mono-label bg-white text-ink border border-white px-6 py-3.5 hover:bg-transparent hover:text-white disabled:opacity-50">
        {loading ? "SENDING..." : "EMAIL ME THIS PLAN"}
      </button>
    </form>
  );
};

export default function PlanBuilder() {
  const [race, setRace] = useState("");
  const [level, setLevel] = useState("");
  const [miles, setMiles] = useState("");
  const [days, setDays] = useState(0);
  const [weeks, setWeeks] = useState(0);
  const [result, setResult] = useState(null);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    setPageMeta("Custom Training Plan Builder | Ninja Run Club", "Answer five questions, get a week-by-week training block for your race: 5K to marathon, HYROX to Ironman. Free, no signup required.");
  }, []);

  const generate = () => {
    const r = buildPlan({ race, level, currentMiles: parseFloat(miles) || 0, days, weeks });
    setResult(r);
    api.get("/articles", { params: { category: r.category } }).then((res) => setArticles(res.data.slice(0, 3))).catch(() => {});
    setTimeout(() => document.getElementById("plan-result")?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  const reset = () => { setResult(null); setRace(""); setLevel(""); setMiles(""); setDays(0); setWeeks(0); };
  const ready = race && level && days && weeks;
  const cfg = race ? RACES[race] : null;

  return (
    <div className="bg-night min-h-screen">
      <header className="px-5 lg:px-10 pt-16 pb-10 border-b border-white/10">
        <Reveal>
          <p className="font-mono-label text-white/40">THE PLAN BUILDER</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-none">FIVE QUESTIONS. ONE BLOCK.</h1>
          <p className="text-white/50 text-sm max-w-lg mt-4">Answer honestly, get a week-by-week training block built on the same structure our coaching guides teach. Free, instant, yours.</p>
        </Reveal>
      </header>

      <div className="px-5 lg:px-10 py-12 max-w-4xl space-y-10">
        <div data-testid="plan-q-race">
          <p className="font-mono-label text-white/40 mb-4">01 · WHAT ARE YOU TRAINING FOR?</p>
          <div className="flex flex-wrap gap-2">
            {Object.keys(RACES).map((r) => (
              <button key={r} data-testid={`plan-race-${r.toLowerCase().replace(/\s/g, "-")}`} onClick={() => { setRace(r); setWeeks(0); }} className={chip(race === r)}>{r.toUpperCase()}</button>
            ))}
          </div>
        </div>

        <div data-testid="plan-q-level">
          <p className="font-mono-label text-white/40 mb-4">02 · WHERE ARE YOU RIGHT NOW?</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(LEVELS).map(([k, v]) => (
              <button key={k} data-testid={`plan-level-${k}`} onClick={() => setLevel(k)} className={chip(level === k)}>{v.label}</button>
            ))}
          </div>
        </div>

        <div data-testid="plan-q-miles">
          <p className="font-mono-label text-white/40 mb-4">03 · CURRENT WEEKLY MILEAGE (ROUGHLY)</p>
          <input data-testid="plan-miles-input" type="number" min="0" max="120" placeholder="15" value={miles} onChange={(e) => setMiles(e.target.value)} className="w-40 bg-transparent border-b border-white/30 py-3 text-2xl font-display placeholder:text-white/20 focus:border-white transition-colors duration-200" />
        </div>

        <div data-testid="plan-q-days">
          <p className="font-mono-label text-white/40 mb-4">04 · RUN DAYS PER WEEK</p>
          <div className="flex flex-wrap gap-2">
            {[3, 4, 5, 6].map((d) => (
              <button key={d} data-testid={`plan-days-${d}`} onClick={() => setDays(d)} className={chip(days === d)}>{d} DAYS</button>
            ))}
          </div>
        </div>

        <div data-testid="plan-q-weeks">
          <p className="font-mono-label text-white/40 mb-4">05 · WEEKS UNTIL RACE DAY {cfg && <span className="text-white/25">({cfg.minWeeks}–{cfg.maxWeeks} RECOMMENDED)</span>}</p>
          <div className="flex flex-wrap gap-2">
            {(cfg ? [cfg.minWeeks, Math.round((cfg.minWeeks + cfg.maxWeeks) / 2), cfg.maxWeeks] : [8, 12, 16]).map((w) => (
              <button key={w} data-testid={`plan-weeks-${w}`} onClick={() => setWeeks(w)} className={chip(weeks === w)} disabled={!cfg}>{w} WEEKS</button>
            ))}
          </div>
        </div>

        <button
          data-testid="plan-generate-button"
          onClick={generate}
          disabled={!ready}
          className="btn-invert font-mono-label bg-white text-ink border border-white px-8 py-4 hover:bg-transparent hover:text-white disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-3"
        >
          BUILD MY BLOCK <ArrowRight size={15} />
        </button>
      </div>

      {result && (
        <div id="plan-result" className="px-5 lg:px-10 py-14 border-t border-white/10" data-testid="plan-result">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="font-mono-label text-white/40">YOUR BLOCK</p>
              <h2 className="font-display text-3xl lg:text-5xl mt-3">{result.race.toUpperCase()} · {result.total} WEEKS · PEAK {result.peakMiles} MI</h2>
              <p className="text-white/50 text-sm mt-3 max-w-xl">{result.days} runs per week. Easy days conversational, quality days on purpose, cutback every fourth week. Set exact paces with the <Link to="/tools" className="link-underline text-white/80">calculators</Link>.</p>
            </div>
            <button onClick={reset} data-testid="plan-reset-button" className="font-mono-label text-white/40 hover:text-white flex items-center gap-2 transition-colors duration-200"><RotateCcw size={13} /> START OVER</button>
          </div>

          <div className="overflow-x-auto mt-10 border border-white/15">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-white text-ink">
                  <th className="text-left font-mono-label px-4 py-3">WEEK</th>
                  <th className="text-left font-mono-label px-4 py-3">PHASE</th>
                  <th className="text-left font-mono-label px-4 py-3">MILES</th>
                  <th className="text-left font-mono-label px-4 py-3">LONG RUN</th>
                  <th className="text-left font-mono-label px-4 py-3">KEY WORK</th>
                </tr>
              </thead>
              <tbody>
                {result.plan.map((w) => (
                  <tr key={w.week} className="border-t border-white/10" data-testid={`plan-week-${w.week}`}>
                    <td className="px-4 py-2.5 font-semibold text-white/90">{w.week}</td>
                    <td className="px-4 py-2.5"><span className={`font-mono-label ${w.phase === "peak" ? "text-white" : "text-white/60"}`}>{w.phase.toUpperCase()}</span></td>
                    <td className="px-4 py-2.5 text-white/70">{w.miles}</td>
                    <td className="px-4 py-2.5 text-white/70">{w.long_run}</td>
                    <td className="px-4 py-2.5 text-white/50">{w.key}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-10 max-w-xl">
            <p className="font-mono-label text-white/40 mb-4">SEND IT TO YOURSELF</p>
            <EmailPlan result={result} />
          </div>

          {articles.length > 0 && (
            <div className="mt-14" data-testid="plan-related-articles">
              <p className="font-mono-label text-white/40 mb-6">READ THE BLOCK'S PLAYBOOK</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
                {articles.map((a) => (
                  <Link key={a.slug} to={`/training/${a.slug}`} className="group bg-night p-8 hover:bg-white hover:text-ink transition-colors duration-300 flex flex-col">
                    <p className="font-mono-label opacity-50">{a.category} · {a.read_time} MIN READ</p>
                    <h3 className="font-display text-xl lg:text-2xl mt-3">{a.title}</h3>
                    <p className="text-sm opacity-50 mt-3 line-clamp-3">{a.excerpt}</p>
                    <span className="font-mono-label opacity-40 group-hover:opacity-100 mt-auto pt-6 flex items-center gap-2">READ IT <ArrowRight size={12} /></span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
